import { FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Activity,
  ArrowLeft,
  AtSign,
  BarChart3,
  Bot,
  BriefcaseBusiness,
  CheckCircle2,
  Eye,
  EyeOff,
  FileText,
  Inbox,
  Landmark,
  ListTodo,
  LockKeyhole,
  LogOut,
  Mail,
  PhoneCall,
  RefreshCw,
  Repeat2,
  ReceiptText,
  Search,
  Send,
  ShieldCheck,
  UserCheck,
  UserPlus,
  Users,
  WalletCards,
} from 'lucide-react';

import LoginBottomIllustration from '../assets/login-bottom-illustration';
import LoginTopIllustration from '../assets/login-top-illustration';
import ZetaLogo from '../assets/zeta-logo';
import type { OpsSession } from './types';
import { api } from './api-client';
import { demoSession, isDemoLogin } from './demo-data';
import {
  buildSessionFromToken,
  errorMessage,
  extractAuthToken,
  extractClaimsToken,
  firstText,
  getPath,
} from './operations-utils';

type LoginStep = 'login' | 'setPassword';
type PasswordField = 'password' | 'newPassword' | 'confirmPassword';
type LoginMutationResult = OpsSession | { passwordSetupRequired: true };

const PASSWORD_SETUP_ERROR_CODES = new Set([
  'U010015',
  'NEW_PASSWORD_REQUIRED',
  'PASSWORD_CHANGE_REQUIRED',
  'PASSWORD_EXPIRED',
]);
const PASSWORD_SETUP_MESSAGE_PARTS = [
  'new password required',
  'password has expired',
  'password expired',
  'temporary password',
  'change password',
];
const AUTHENTICATION_ERROR_MESSAGE =
  'The username or password you entered does not match our records.';

function invitationUsername() {
  if (typeof window === 'undefined') {
    return '';
  }
  return new URLSearchParams(window.location.search).get('username') ?? '';
}

function isPasswordSetupResult(
  result: LoginMutationResult,
): result is { passwordSetupRequired: true } {
  return 'passwordSetupRequired' in result;
}

function passwordSetupRequiredResult(): LoginMutationResult {
  return { passwordSetupRequired: true };
}

function isPasswordPolicyValid(value: string) {
  return (
    value.length >= 8 &&
    /[a-z]/.test(value) &&
    /[A-Z]/.test(value) &&
    /\d/.test(value) &&
    /[^A-Za-z0-9]/.test(value)
  );
}

function loginDataRequiresPasswordSetup(data: unknown) {
  const candidates = [
    data,
    getPath(data, 'response'),
    getPath(data, 'responseItem'),
    getPath(data, 'data'),
    getPath(data, 'responseItems.0'),
  ];

  return candidates.some((candidate) => {
    const challenge = firstText(
      getPath(candidate, 'challengeName'),
      getPath(candidate, 'challenge'),
      getPath(candidate, 'authChallenge'),
      getPath(candidate, 'status'),
      getPath(candidate, 'errorCode'),
      getPath(candidate, 'error.errorCode'),
      getPath(candidate, 'error.0.errorCode'),
      getPath(candidate, 'message'),
      getPath(candidate, 'error.message'),
    ).toUpperCase();
    const hasPasswordFlag =
      Boolean(getPath(candidate, 'newPasswordRequired')) ||
      Boolean(getPath(candidate, 'passwordChangeRequired')) ||
      Boolean(getPath(candidate, 'changePasswordRequired')) ||
      Boolean(getPath(candidate, 'passwordExpired'));

    return (
      hasPasswordFlag ||
      PASSWORD_SETUP_ERROR_CODES.has(challenge) ||
      challenge.includes('NEW_PASSWORD_REQUIRED') ||
      challenge.includes('PASSWORD_CHANGE_REQUIRED')
    );
  });
}

function errorRequiresPasswordSetup(error: unknown) {
  const errorCode = firstText(
    getPath(error, 'response.data.error.errorCode'),
    getPath(error, 'response.data.error.0.errorCode'),
    getPath(error, 'response.data.errorCode'),
    getPath(error, 'response.data.code'),
    getPath(error, 'code'),
  ).toUpperCase();

  if (
    PASSWORD_SETUP_ERROR_CODES.has(errorCode) ||
    loginDataRequiresPasswordSetup(getPath(error, 'response.data')) ||
    loginDataRequiresPasswordSetup(error)
  ) {
    return true;
  }

  const message = errorMessage(error).toLowerCase();
  return PASSWORD_SETUP_MESSAGE_PARTS.some((part) => message.includes(part));
}

function sessionFromLoginResponse(data: unknown, username: string) {
  const authToken = extractAuthToken(data);
  if (!authToken) {
    throw new Error('Login completed but no access token was returned.');
  }
  return buildSessionFromToken(
    extractClaimsToken(data) || authToken,
    username,
    authToken,
    data,
  );
}

export function LoginScreen({
  onLogin,
}: {
  onLogin: (session: OpsSession) => void;
}) {
  const [loginStep, setLoginStep] = useState<LoginStep>('login');
  const [form, setForm] = useState(() => ({
    username: invitationUsername(),
    password: '',
    newPassword: '',
    confirmPassword: '',
  }));
  const [visiblePasswords, setVisiblePasswords] = useState<
    Record<PasswordField, boolean>
  >({
    password: false,
    newPassword: false,
    confirmPassword: false,
  });
  const loginPath = import.meta.env.VITE_OPS_LOGIN_PATH || undefined;
  const resetPasswordPath =
    import.meta.env.VITE_OPS_RESET_PASSWORD_PATH || undefined;
  const passwordPolicySatisfied = isPasswordPolicyValid(form.newPassword);
  const newPasswordMatchesTemporary =
    Boolean(form.newPassword) && form.newPassword === form.password;
  const passwordSetupInvalid =
    !form.username.trim() ||
    !form.password ||
    !form.newPassword ||
    !form.confirmPassword ||
    !passwordPolicySatisfied ||
    newPasswordMatchesTemporary ||
    form.newPassword !== form.confirmPassword;

  const requestSession = async (
    password: string,
  ): Promise<LoginMutationResult> => {
    const username = form.username.trim();
    const response = await api.login(
      {
        username,
        password,
      },
      loginPath,
    );

    if (loginDataRequiresPasswordSetup(response.data)) {
      return passwordSetupRequiredResult();
    }

    return sessionFromLoginResponse(response.data, username);
  };

  const loginMutation = useMutation({
    mutationFn: async () => {
      if (isDemoLogin(form.username, form.password)) {
        return demoSession;
      }

      try {
        return await requestSession(form.password);
      } catch (error) {
        if (errorRequiresPasswordSetup(error)) {
          return passwordSetupRequiredResult();
        }
        throw error;
      }
    },
    onSuccess: (result) => {
      if (isPasswordSetupResult(result)) {
        setLoginStep('setPassword');
        return;
      }
      onLogin(result);
    },
  });

  const setupPasswordMutation = useMutation({
    mutationFn: async () => {
      const username = form.username.trim();
      await api.resetPassword(
        {
          username,
          oldPassword: form.password,
          newPassword: form.newPassword,
        },
        resetPasswordPath,
      );

      const result = await requestSession(form.newPassword);
      if (isPasswordSetupResult(result)) {
        throw new Error(
          'Your password was changed, but the operations API still requires a password update. Please try logging in again.',
        );
      }
      return result;
    },
    onSuccess: onLogin,
  });

  const updateForm = (patch: Partial<typeof form>) => {
    loginMutation.reset();
    setupPasswordMutation.reset();
    setForm((current) => ({ ...current, ...patch }));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loginStep === 'setPassword') {
      setupPasswordMutation.mutate();
      return;
    }
    loginMutation.mutate();
  };

  const resetToLogin = () => {
    loginMutation.reset();
    setupPasswordMutation.reset();
    setLoginStep('login');
    setForm((current) => ({
      ...current,
      newPassword: '',
      confirmPassword: '',
    }));
  };

  const togglePasswordVisibility = (field: PasswordField) => {
    setVisiblePasswords((current) => ({
      ...current,
      [field]: !current[field],
    }));
  };

  const passwordAdornment = (field: PasswordField, label: string) => {
    const isVisible = visiblePasswords[field];
    const actionLabel = isVisible ? `Hide ${label}` : `Show ${label}`;

    return {
      endAdornment: (
        <InputAdornment position="end">
          <Tooltip title={actionLabel}>
            <IconButton
              aria-label={actionLabel}
              edge="end"
              onClick={() => togglePasswordVisibility(field)}
              onMouseDown={(event) => event.preventDefault()}
            >
              {isVisible ? <EyeOff size={18} /> : <Eye size={18} />}
            </IconButton>
          </Tooltip>
        </InputAdornment>
      ),
    };
  };

  const activeError = loginMutation.error ?? setupPasswordMutation.error;
  const isSubmitting =
    loginStep === 'setPassword'
      ? setupPasswordMutation.isPending
      : loginMutation.isPending;
  const submitDisabled =
    isSubmitting ||
    (loginStep === 'setPassword'
      ? passwordSetupInvalid
      : !form.username.trim() || !form.password);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#f7f8fb',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'absolute',
          left: '34%',
          top: 24,
          zIndex: 2,
          opacity: 0.88,
        }}
      >
        <LoginTopIllustration />
      </Box>
      <Box
        sx={{
          display: { xs: 'none', lg: 'block' },
          position: 'absolute',
          left: 72,
          bottom: -8,
          zIndex: 2,
          opacity: 0.9,
        }}
      >
        <LoginBottomIllustration />
      </Box>

      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          width: '50%',
          flexBasis: '50%',
          minHeight: '100vh',
          flexShrink: 0,
          background:
            'linear-gradient(135deg, #2e1065 0%, #4c1d95 50%, #0f766e 100%)',
          color: '#fff',
          position: 'relative',
          p: { lg: 6, xl: 8 },
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 3 }}>
          <ZetaLogo />
        </Box>

        <Stack
          spacing={4.5}
          sx={{ position: 'relative', zIndex: 3, maxWidth: 520 }}
        >
          <Stack spacing={2}>
            <Chip
              icon={<ShieldCheck size={14} />}
              label="Secure operations portal"
              sx={{
                alignSelf: 'flex-start',
                bgcolor: 'rgba(255,255,255,0.14)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.22)',
                '& .MuiChip-icon': { color: '#bbf7d0' },
              }}
            />
            <Typography
              sx={{
                fontSize: { lg: 42, xl: 48 },
                lineHeight: 1.06,
                fontWeight: 800,
                letterSpacing: 0,
                maxWidth: 500,
              }}
            >
              Investment operations, risk, and service in one workspace.
            </Typography>
            <Typography
              sx={{
                fontSize: 16,
                lineHeight: 1.75,
                color: 'rgba(255,255,255,0.78)',
                maxWidth: 470,
              }}
            >
              Sign in to manage KYC decisions, workflow queues, client care,
              platform settings, and audit-ready operational controls.
            </Typography>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 1.5,
              maxWidth: 500,
            }}
          >
            {[
              { label: 'KYC', value: 'Review' },
              { label: 'Ops', value: 'Queues' },
              { label: 'Audit', value: 'Trace' },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  border: '1px solid rgba(255,255,255,0.18)',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  p: 1.5,
                  backdropFilter: 'blur(8px)',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.64)',
                    fontWeight: 700,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography sx={{ mt: 0.5, fontSize: 18, fontWeight: 800 }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Stack>

        <Stack
          spacing={0.75}
          sx={{ position: 'relative', zIndex: 3, maxWidth: 430 }}
        >
          <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.68)' }}>
            Need help accessing your account?
          </Typography>
          <Typography
            component="a"
            href="mailto:support@zetawealth.co.uk"
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: '#d8b4fe',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            support@zetawealth.co.uk
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={{
          display: { xs: 'flex', lg: 'none' },
          px: 3,
          pt: 3,
          pb: 2,
          bgcolor: '#2e1065',
          color: '#fff',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <ZetaLogo />
        <Chip
          size="small"
          icon={<ShieldCheck size={13} />}
          label="Secure"
          sx={{
            bgcolor: 'rgba(255,255,255,0.14)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.2)',
            '& .MuiChip-icon': { color: '#bbf7d0' },
          }}
        />
      </Box>

      <Box
        sx={{
          flex: { xs: 1, lg: '0 0 50%' },
          width: { xs: '100%', lg: '50%' },
          p: { xs: 2.5, sm: 4, md: 6, lg: 8 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: { xs: 'calc(100vh - 84px)', lg: '100vh' },
          background: {
            xs: '#f7f8fb',
            lg: 'linear-gradient(180deg, #ffffff 0%, #f7f8fb 100%)',
          },
        }}
      >
        <Card
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: '100%',
            maxWidth: 520,
            bgcolor: '#fff',
            color: '#231942',
            border: '1px solid rgba(35, 25, 66, 0.1)',
            boxShadow: '0 24px 70px rgba(35, 25, 66, 0.12)',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 3, sm: 4.5 },
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: '#fbfaff',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
            >
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.light',
                    color: 'primary.dark',
                    width: 34,
                    height: 34,
                  }}
                >
                  <LockKeyhole size={18} />
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    Zeta Operations
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Authorised access only
                  </Typography>
                </Box>
              </Stack>
              <Chip
                size="small"
                label={
                  loginStep === 'setPassword' ? 'Change password' : 'Sign in'
                }
                color="primary"
                variant="outlined"
              />
            </Stack>
          </Box>

          <CardContent
            sx={{
              p: { xs: 3, sm: 4.5 },
              '& .MuiInputLabel-root': { color: 'rgba(35, 25, 66, 0.58)' },
              '& .MuiInputLabel-root.Mui-focused': { color: '#7c3aed' },
              '& .MuiInputBase-input': { color: '#231942' },
              '& .MuiOutlinedInput-root': { bgcolor: '#fff' },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(35, 25, 66, 0.18)',
              },
              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                { borderColor: 'rgba(124,58,237,0.5)' },
            }}
          >
            <Stack spacing={3.25}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {loginStep === 'setPassword'
                    ? 'Change your password'
                    : 'Welcome back'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  {loginStep === 'setPassword'
                    ? 'Enter your temporary password and create a permanent password for future access.'
                    : 'Use your operations credentials to continue to the workspace.'}
                </Typography>
              </Box>

              {loginStep === 'setPassword' ? (
                <Alert severity="info">
                  Your first login requires a password change before the portal
                  opens.
                </Alert>
              ) : null}
              {activeError ? (
                <Alert severity="error">{AUTHENTICATION_ERROR_MESSAGE}</Alert>
              ) : null}

              <Stack spacing={2.25}>
                <TextField
                  label="Username"
                  autoComplete="username"
                  value={form.username}
                  onChange={(event) =>
                    updateForm({ username: event.target.value })
                  }
                  fullWidth
                  id="userNameOrEmail"
                />
                <TextField
                  label={
                    loginStep === 'setPassword'
                      ? 'Temporary password'
                      : 'Password'
                  }
                  type={visiblePasswords.password ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={form.password}
                  onChange={(event) =>
                    updateForm({ password: event.target.value })
                  }
                  fullWidth
                  id="password"
                  slotProps={{
                    input: passwordAdornment(
                      'password',
                      loginStep === 'setPassword'
                        ? 'temporary password'
                        : 'password',
                    ),
                  }}
                />
                {loginStep === 'setPassword' ? (
                  <>
                    <TextField
                      label="New password"
                      type={visiblePasswords.newPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={form.newPassword}
                      onChange={(event) =>
                        updateForm({ newPassword: event.target.value })
                      }
                      error={
                        Boolean(form.newPassword) &&
                        (!passwordPolicySatisfied ||
                          newPasswordMatchesTemporary)
                      }
                      helperText={
                        newPasswordMatchesTemporary
                          ? 'Choose a password different from the temporary password.'
                          : 'Use at least 8 characters with uppercase, lowercase, number, and symbol.'
                      }
                      fullWidth
                      id="newPassword"
                      slotProps={{
                        input: passwordAdornment('newPassword', 'new password'),
                      }}
                    />
                    <TextField
                      label="Confirm new password"
                      type={
                        visiblePasswords.confirmPassword ? 'text' : 'password'
                      }
                      autoComplete="new-password"
                      value={form.confirmPassword}
                      onChange={(event) =>
                        updateForm({ confirmPassword: event.target.value })
                      }
                      error={
                        Boolean(form.confirmPassword) &&
                        form.confirmPassword !== form.newPassword
                      }
                      helperText={
                        form.confirmPassword &&
                        form.confirmPassword !== form.newPassword
                          ? 'Passwords must match.'
                          : ' '
                      }
                      fullWidth
                      id="confirmNewPassword"
                      slotProps={{
                        input: passwordAdornment(
                          'confirmPassword',
                          'confirmation password',
                        ),
                      }}
                    />
                  </>
                ) : null}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitDisabled}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : loginStep === 'setPassword' ? (
                      <ShieldCheck size={18} />
                    ) : (
                      <LockKeyhole size={18} />
                    )
                  }
                  id="login"
                  sx={{
                    py: 1.35,
                    boxShadow: '0 12px 30px rgba(124, 58, 237, 0.24)',
                  }}
                >
                  {isSubmitting
                    ? 'Working...'
                    : loginStep === 'setPassword'
                      ? 'Change Password and Login'
                      : 'Login'}
                </Button>
                {loginStep === 'setPassword' ? (
                  <Button
                    type="button"
                    variant="text"
                    onClick={resetToLogin}
                    startIcon={<ArrowLeft size={18} />}
                  >
                    Back to Login
                  </Button>
                ) : null}
              </Stack>

              <Divider />
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
              >
                <Typography variant="caption" color="text.secondary">
                  Protected by role-based access controls
                </Typography>
                <Typography
                  component="a"
                  href="mailto:support@zetawealth.co.uk"
                  variant="caption"
                  sx={{
                    color: 'primary.dark',
                    fontWeight: 700,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Get access support
                </Typography>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
